import { styled } from '@mui/material';
import { Box, TableCell, TableFooter, TableRow } from '@mui/material';

/**
 * Styled TableFooter component with theme integration
 * Provides consistent styling that matches the table's hover state
 */
export const StyledTableFooter = styled(TableFooter)(({ theme }) => ({
  '& .MuiTableRow-root': {
    backgroundColor: theme.palette.action.hover,
    borderTop: `1px solid ${theme.palette.divider}`,

    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

/**
 * Styled TableRow for footer content
 * Applies consistent spacing and hover effects
 */
export const StyledFooterRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderTop: `1px solid ${theme.palette.divider}`,

  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },

  '& .MuiTableCell-root': {
    borderBottom: 'none',
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

/**
 * Styled TableCell for footer content
 * Provides consistent typography and spacing
 */
export const StyledFooterCell = styled(TableCell)(({ theme }) => ({
  borderBottom: 'none',
  fontWeight: theme.typography.fontWeightMedium,
  backgroundColor: 'inherit',
  width: '100%',
  minWidth: '100%',

  '&.footer-label': {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
  },

  '&.footer-value': {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
  },

  '&.footer-total': {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
  },
}));

/**
 * Container for standalone footer section
 * Used when footer is positioned outside the table
 */
export const StyledFooterSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(2),

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
}));

/**
 * Container for total rows display
 */
export const StyledTotalContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),

  '& .total-label': {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
  },

  '& .total-value': {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
  },
}));

/**
 * Container for summary values display
 */
export const StyledSummaryContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  flexWrap: 'nowrap',

  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
}));

/**
 * Individual summary item styling
 */
export const StyledSummaryItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),

  '& .summary-label': {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.body2.fontSize,
  },

  '& .summary-value': {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body2.fontSize,
  },
}));

/**
 * Flex container for footer content inside the single spanning cell.
 * Ensures a single row on desktop, wrapping only on mobile.
 */
export const StyledFooterContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: theme.spacing(2),
  flexWrap: 'nowrap',
  padding: theme.spacing(1, 2),

  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
  },
}));

/**
 * Sticky footer container for tables with sticky footer enabled
 */
export const StyledStickyFooter = styled(Box)(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  zIndex: theme.zIndex.appBar - 1,
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2],
}));
